'use client'

import { useState, useRef, type Dispatch, type SetStateAction } from 'react'
import type { NavMenuFragment, ProductFragment } from '@/lib/types/datocms/graphql/generated'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'

import clsx from 'clsx'
import Icon from '@/components/svg/icon'
import IconButton from '@/components/ui/IconButton'
import NavSubMenu from './NavSubMenu'
import { useIsResizing } from '@/hooks/useIsResizing'

export default function NavMenu({
	title,
	subMenu,
	isMegaMenuOpen,
	setIsMegaMenuOpen,
	setShowMobileMenu,
	activeMegaMenu,
	setActiveMegaMenu,
	setCurrentDropdown,
	products,
}: Readonly<
	NavMenuFragment & {
		isMegaMenuOpen: boolean
		setIsMegaMenuOpen: Dispatch<SetStateAction<boolean>>
		setShowMobileMenu: Dispatch<SetStateAction<boolean>>
		setActiveMegaMenu: Dispatch<SetStateAction<string | null | undefined>>
		activeMegaMenu: string | null | undefined
		setCurrentDropdown: Dispatch<SetStateAction<string | null | undefined>>
		products: ProductFragment[]
	}
>) {
	const isActive = activeMegaMenu === title
	const megaMenuRef = useRef(null)
	const titleButtonRef = useRef<HTMLButtonElement | null>(null)
	const firstTabRef = useRef<HTMLButtonElement | null>(null)
	const [isImageModalOpen, setIsImageModalOpen] = useState(false)
	const isResizing = useIsResizing()

	const handleTitleClick = (clickedTitle: string | null | undefined) => {
		setActiveMegaMenu(clickedTitle)
		setIsMegaMenuOpen(true)
	}

	return (
		<div>
			{/* Desktop: Top Bar Buttons Among Links & Dropdowns */}
			{/* Mobile: Slide 1 */}
			<div className="max-lg:z-0">
				{/* Eg. "Trailers" or "Head Ramps" - Button opens Mega Menu*/}
				<button
					ref={titleButtonRef}
					className={clsx(
						'flex items-center justify-between gap-1 p-2',
						!isResizing && 'transition-colors duration-300',
						isActive && 'lg:text-blue',
						'max-lg:font-semibold max-lg:px-3 max-lg:py-5 max-lg:border-b max-lg:w-full max-lg:text-left',
						'max-lg:hover:bg-white-frosted'
					)}
					onMouseDown={() => {
						setCurrentDropdown('')
						handleTitleClick(title)
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault()
							handleTitleClick(title)
						}
					}}
					role="button"
					aria-expanded={isActive}
					aria-controls={`mega-menu-${title}`}
				>
					{title}
					<Icon icon="Chevron" className="lg:hidden text-blue" />
				</button>
			</div>

			{/* Desktop: Mega Menu */}
			{/* Mobile: Slide 2 */}
			<TabGroup
				ref={megaMenuRef}
				className={clsx(
					'fixed inset-0 lg:top-[--nav-height] bg-white h-[calc(100dvh---nav-height)] overflow-hidden lg:-z-10',
					!isResizing &&
						'max-lg:transition-transform max-lg:duration-500 max-lg:ease-s lg:transition-opacity lg:duration-500 lg:m-ease',
					activeMegaMenu ? 'max-lg:translate-x-0' : 'max-lg:translate-x-full',
					isActive && isMegaMenuOpen
						? 'lg:opacity-100 max-lg:z-10'
						: `lg:opacity-0 lg:pointer-events-none max-lg:z-0 ${!isResizing && 'lg:delay-300'}`
				)}
			>
				{/* Mobile: Back to Slide 1 Button */}
				<button
					className="lg:hidden flex gap-2 py-5 px-3 w-full border-b hover:bg-gray-light/40 transition-colors duration-300"
					onClick={() => setActiveMegaMenu('')}
				>
					<Icon icon="Arrow" className="rotate-180" />
					<span className="text-lg font-semibold text-blue-dark/60">Back</span>
				</button>

				<div className="flex justify-between items-center common-right-padding bg-white lg:border-b z-10 border-gray-light">
					{/* Tab List: Car Haulers, International, atc... */}
					<TabList
						className={clsx(
							'flex',
							!isResizing && 'lg:transition-transform lg:duration-300 lg:m-ease',
							// isActive
							isMegaMenuOpen
								? 'lg:translate-y-0'
								: `lg:-translate-y-full ${!isResizing && 'lg:delay-300'}`
						)}
					>
						{subMenu.map((submenu, idx) => (
							<Tab
								key={submenu.id as string}
								ref={idx === 0 ? firstTabRef : null}
								className={({ selected }) =>
									clsx(
										'relative text-black uppercase font-semibold tracking-snug text-xs px-6 lg:px-9 py-6 lg:py-7 rounded-none lg:border-t-2',
										!isResizing && 'transition-all duration-300',
										selected
											? 'lg:border-t-blue bg-white-frosted'
											: 'lg:border-t-transparent bg-white after:content-[""] after:absolute after:inset-y-0 lg:after:-top-0.5 after:right-0 after:w-px lg:after:bg-gray-light max-lg:after:bg-gray-light/50'
									)
								}
							>
								{submenu.title}
							</Tab>
						))}
					</TabList>

					{/* Close Mega Menu Button */}
					<IconButton
						icon="Close"
						className="ml-auto [&>svg]:w-4 [&>svg]:h-4 p-1 max-lg:hidden"
						onClick={() => {
							setIsMegaMenuOpen(false)
							setActiveMegaMenu('')
						}}
						role="button"
						aria-label="Close Mega Menu"
					/>
				</div>

				{/* Tab Panels */}
				<TabPanels
					className={clsx(
						'lg:relative h-[calc(100dvh-207px)] lg:h-[calc(100dvh-156px)] -z-10',
						!isResizing && 'lg:transition-[transform,opacity] lg:duration-500 lg:m-ease',
						isMegaMenuOpen
							? 'lg:translate-y-0 lg:opacity-100 lg:delay-300'
							: 'lg:-translate-y-4 lg:opacity-0'
					)}
					data-testid={`dropdown-menu-${title}`}
				>
					{subMenu.map(
						(submenu) =>
							submenu.category && (
								<TabPanel key={submenu.id as string} className="h-full">
									<NavSubMenu
										{...submenu}
										setIsImageModalOpen={setIsImageModalOpen}
										isImageModalOpen={isImageModalOpen}
										subCategories={submenu.category?.subCategories}
										setIsMegaMenuOpen={setIsMegaMenuOpen}
										setShowMobileMenu={setShowMobileMenu}
										products={products}
									/>
								</TabPanel>
							)
					)}
				</TabPanels>
			</TabGroup>
		</div>
	)
}
